import Marker from '../Marker';
import subpixel from '../util/canvasSubpixelOffset';

/** Candlestick marker */
class CandlestickMarker extends Marker {
	getDefaultParams() {
		return {
			fillType: 'hollow', // "hollow", "filled"
			lineColor: '#666',
			fillColorNeg: '#000',
			fillColorPos: '#fff'
		};
	}
	/**
	 * calculate dynamic bar width based on data period and panel domain
	 * (method used by more than one marker subclass so it lives here)
	 * @param {Panel} panel
	 * @return {int}
	 */
	getBarWidth(panel, min = null, max = null) {
		if (!min && !max) [min, max] = panel.xAxis.primaryScale.domain();
		const { width } = panel.size;
		let len = max - min;
		const tolerance = panel.core.params.candleWidthTolerance;
		let w0;

		switch (panel.core.params.dataPeriod) {
			case 'Year':
				len /= 365;
				break;
			case 'Month':
				len /= 15;
				break;
			case 'Week':
				len /= 3;
				break;
			case 'Day':
				break;
			case 'Hour':
				len /= 30;
				break;
			case 'Minute':
				len /= panel.core.params.dataInterval
					? panel.core.params.dataInterval
					: 1;
				break;
		}

		w0 = Math.min(30, (0.6 * width) / len);

		// STS-1340
		if (tolerance !== 0 && w0 < tolerance) w0 = 1;

		return w0;
	}

	render(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		fillColorPos,
		fillColorNeg,
		fillColorNeutral,
		lineStyle
	) {
		const w0 = this.getBarWidth(panel);
		const w1 = w0 / 2;
		const d = []; // date
		const o = []; // open
		const h = []; // high
		const l = []; // low
		const c = []; // close
		let x = 0;
		let dataRow = [];
		const isFilled = this.params.fillType === 'filled';
		const isJapaneseFill = this.params.fillRule === 'openClose';
		const isMF =
			panel.core.xref[panel.core.params.symbol] &&
			panel.core.xref[panel.core.params.symbol].issueType === 'MF';
		const showCandleBody = isMF || w0 > 1 || data.length < 100; // conceal body on well-populated datasets with thin candles, but don't do it for funds which have no vertical height on a Daily period

		fillColorNeutral = fillColorNeutral || '#999999';

		// pos wicks
		ctx.strokeStyle = fillColorPos;
		ctx.lineWidth = lineStyle;
		ctx.beginPath();

		for (x = 0; x < data.length; x++) {
			dataRow = data[x];
			// 0 = date
			// 1 = close
			// 2 = open
			// 3 = high
			// 4 = low
			// 5 = diff (change from previous)
			// 6 = hollow/filled
			// 7 = for "filled mode" only, red/green color

			// normally already available, but this can happen for comparisons
			if (typeof dataRow[5] === 'undefined') {
				// get diff (for determining color)
				if (x > 0) {
					dataRow[5] = dataRow[1] - data[x - 1][1];
				} else {
					dataRow[5] = 0;
				}
			}

			// cache xScale/yScale lookups
			// Math.round to ensure array of ints instead of doubles
			d[x] = Math.round(xScale(dataRow[0]));
			o[x] = Math.round(yScale(dataRow[2]));
			h[x] = Math.round(yScale(dataRow[3]));
			l[x] = Math.round(yScale(dataRow[4]));
			c[x] = Math.round(yScale(dataRow[1]));

			// "filled mode" candles derive color from open/close instead of close/close
			if (isFilled) {
				if (dataRow[1] > dataRow[2]) {
					dataRow[7] = 1; // pos
				} else if (dataRow[1] < dataRow[2]) {
					dataRow[7] = -1; // neg
				} else {
					dataRow[7] = 0; // unch
				}
			}

			// normally already available, but this can happen for comparisons
			if (typeof dataRow[6] === 'undefined') {
				// get candle hollow bool
				dataRow[6] = dataRow[1] > dataRow[2] ? 1 : 0;
			}

			if (
				(!isFilled && dataRow[5] > 0) ||
				(isFilled && dataRow[7] === 1)
			) {
				if (showCandleBody) {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], Math.min(o[x], c[x]));
					subpixel.moveTo(ctx, d[x], Math.max(o[x], c[x]));
					subpixel.lineTo(ctx, d[x], l[x]);
				} else {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], l[x]);
				}
			}
		}
		ctx.closePath();
		ctx.stroke();

		// neg wicks
		ctx.strokeStyle = fillColorNeg;
		ctx.beginPath();

		for (x = 0; x < data.length; x++) {
			dataRow = data[x];

			if (
				(!isFilled && dataRow[5] < 0) ||
				(isFilled && dataRow[7] === -1)
			) {
				if (showCandleBody) {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], Math.min(o[x], c[x]));
					subpixel.moveTo(ctx, d[x], Math.max(o[x], c[x]));
					subpixel.lineTo(ctx, d[x], l[x]);
				} else {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], l[x]);
				}
			}
		}
		ctx.closePath();
		ctx.stroke();

		// unchanged wicks
		ctx.strokeStyle = fillColorNeutral;
		ctx.beginPath();

		for (x = 0; x < data.length; x++) {
			dataRow = data[x];

			if (
				(!isFilled && dataRow[5] === 0) ||
				(isFilled && dataRow[7] === 0)
			) {
				if (showCandleBody) {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], Math.min(o[x], c[x]));
					subpixel.moveTo(ctx, d[x], Math.max(o[x], c[x]));
					subpixel.lineTo(ctx, d[x], l[x]);
				} else {
					subpixel.moveTo(ctx, d[x], h[x]);
					subpixel.lineTo(ctx, d[x], l[x]);
				}
			}
		}
		ctx.closePath();
		ctx.stroke();

		// draw candle body.  there are two types of filled candles -
		// 1) default - compare close to previous close
		// 2) "filled" candle mode - compare close to open
		if (showCandleBody) {
			// unchanged filled
			ctx.fillStyle = fillColorNeutral;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (isFilled) {
					// close === open
					if (dataRow[1] === dataRow[2]) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				} else if (dataRow[5] === 0) {
					// regular filled
					if (dataRow[6] === 0) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.fill();

			// unchanged hollow
			ctx.strokeStyle = fillColorNeutral;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (!isFilled && dataRow[5] === 0) {
					if (dataRow[6] !== 0 && dataRow[6] === 1) {
						subpixel.hollowRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.stroke();

			// pos filled
			ctx.fillStyle = fillColorPos;
			ctx.strokeStyle = fillColorPos;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (isFilled) {
					// close > open
					if (dataRow[1] > dataRow[2]) {
						if (isJapaneseFill) {
							// special case here - would normally be a pos fill, but this style uses a pos hollow (NXC-797)

							subpixel.hollowRect(
								ctx,
								d[x] - w1,
								o[x],
								w0,
								c[x] - o[x]
							);
						} else {
							subpixel.fillRect(
								ctx,
								d[x] - w1,
								o[x],
								w0,
								c[x] - o[x]
							);
						}
					}
				} else if (dataRow[5] > 0) {
					// regular filled
					if (dataRow[6] === 0) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			if (isJapaneseFill) {
				ctx.stroke();
			} else {
				ctx.fill();
			}

			// pos hollow
			ctx.strokeStyle = fillColorPos;
			ctx.beginPath();
			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (!isFilled && dataRow[5] > 0) {
					if (
						dataRow[6] !== 0 &&
						dataRow[5] > 0 &&
						dataRow[6] === 1
					) {
						subpixel.hollowRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.stroke();

			// neg filled
			ctx.fillStyle = fillColorNeg;
			ctx.beginPath();

			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (isFilled) {
					// close < open
					if (dataRow[1] < dataRow[2]) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				} else if (dataRow[5] < 0) {
					// regular filled
					if (dataRow[6] === 0) {
						subpixel.fillRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.fill();

			// neg hollow
			ctx.strokeStyle = fillColorNeg;
			ctx.beginPath();
			for (x = 0; x < data.length; x++) {
				dataRow = data[x];

				if (!isFilled && dataRow[5] < 0) {
					// regular filled
					if (dataRow[6] !== 0) {
						subpixel.hollowRect(ctx, d[x] - w1, o[x], w0, c[x] - o[x]);
					}
				}
			}
			ctx.closePath();
			ctx.stroke();
		}
	}

	renderOHLC(panel, ctx, data, xScale, yScale, lineColor, markerType, lineStyle) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		// 0 = date
		// 1 = close
		// 2 = open
		// 3 = high
		// 4 = low

		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		ctx.lineWidth = lineStyle;
		ctx.strokeStyle = lineColor || this.params.lineColor;
		ctx.beginPath();

		for (x = 0; x < xLen; x++) {
			this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
		}

		ctx.stroke();
	}

	renderOHLCNewHighs(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		lineColorUp,
		lineColorNeutral = '#999999',
		markerType = 'ohlc',
		lineStyle
	) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		let currentHigh = -Infinity;
		const indiciesLessThanCurrentHigh = [];

		/**
		 * Indices for elements in data array:
		 * 0 = date
		 * 1 = close
		 * 2 = open
		 * 3 = high
		 * 4 = low
		 */

		// Scale all of the dates
		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		// draw in three passes, one per color, for better canvas performance

		ctx.lineWidth = lineStyle;

		// Draw new highs
		ctx.strokeStyle = lineColorUp;
		ctx.beginPath();

		for (x = 0; x < xLen; x++) {
			if (
				d[x] + w0 > 0 && // Only check data points on the chart
				data[x][1] > currentHigh
			) {
				currentHigh = data[x][1];
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			} else {
				indiciesLessThanCurrentHigh.push(x);
			}
		}

		ctx.stroke();

		// Draw everything else
		ctx.strokeStyle = lineColorNeutral;
		ctx.beginPath();

		for (const idx of indiciesLessThanCurrentHigh) {
			this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, idx);
		}

		ctx.stroke();
	}

	renderOHLCNewLows(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		lineColorDown,
		lineColorNeutral = '#999999',
		markerType = 'ohlc',
		lineStyle
	) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		let currentLow = Infinity;
		const indiciesGreaterThanCurrentlow = [];

		/**
		 * Indices for elements in data array:
		 * 0 = date
		 * 1 = close
		 * 2 = open
		 * 3 = high
		 * 4 = low
		 */

		// Scale all of the dates
		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		ctx.lineWidth = lineStyle;

		// Draw new lows
		ctx.strokeStyle = lineColorDown;
		ctx.beginPath();

		for (x = 0; x < xLen; x++) {
			if (
				d[x] + w0 > 0 && // Only check data points on the chart
				data[x][1] < currentLow
			) {
				currentLow = data[x][1];
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			} else {
				indiciesGreaterThanCurrentlow.push(x);
			}
		}

		ctx.stroke();

		// Draw everything else
		ctx.strokeStyle = lineColorNeutral;
		ctx.beginPath();

		for (const idx of indiciesGreaterThanCurrentlow) {
			this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, idx);
		}

		ctx.stroke();
	}

	renderOHLCPosNeg(
		panel,
		ctx,
		data,
		xScale,
		yScale,
		lineColorUp,
		lineColorDown,
		lineColorNeutral,
		markerType,
		lineStyle
	) {
		const w0 = this.getBarWidth(panel) * 0.7;
		const d = [];
		let x;
		const xLen = data.length;

		lineColorNeutral = lineColorNeutral || '#999999';

		// 0 = date
		// 1 = close
		// 2 = open
		// 3 = high
		// 4 = low

		for (x = 0; x < xLen; x++) {
			d[x] = xScale(data[x][0]);
		}

		// draw in three passes, one per color, for better canvas performance

		ctx.lineWidth = lineStyle;
		ctx.strokeStyle = lineColorNeutral;
		ctx.beginPath();

		// unchanged
		for (x = 0; x < xLen; x++) {
			if (x === 0 || data[x][1] === data[x - 1][1]) {
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			}
		}

		ctx.stroke();
		ctx.strokeStyle = lineColorUp;
		ctx.beginPath();

		// pos
		for (x = 1; x < xLen; x++) {
			if (data[x][1] > data[x - 1][1]) {
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			}
		}

		ctx.stroke();
		ctx.strokeStyle = lineColorDown;
		ctx.beginPath();

		// neg
		for (x = 1; x < xLen; x++) {
			if (data[x][1] < data[x - 1][1]) {
				this.drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x);
			}
		}

		ctx.stroke();
	}

	drawOHLC(panel, ctx, d, yScale, data, w0, markerType, x) {
		// vert
		subpixel.moveTo(ctx, d[x], yScale(data[x][3]));
		subpixel.lineTo(ctx, d[x], yScale(data[x][4]));

		if (markerType === 'ohlc') {
			// open
			subpixel.moveTo(ctx, d[x] - w0, yScale(data[x][2]));
			subpixel.lineTo(ctx, d[x], yScale(data[x][2]));
		}

		// close
		subpixel.moveTo(ctx, d[x], yScale(data[x][1]));
		subpixel.lineTo(ctx, d[x] + w0, yScale(data[x][1]));
	}
}

export default CandlestickMarker;
